import React from 'react';
import { Column } from 'react-table';
import { FormattedMessage } from 'react-intl';
import Highlighter from 'react-highlight-words';
import { SortType } from '../../../../common/enums/SortType';
import { formatDate, getFullYear } from '../../../../common/helpers/dateHelper';
import { IBindingCallback1 } from '../../../../common/models/callback';
import { IProspectStatus } from '../../../../common/models/prospect/IProspectStatus';
import { ProspectStatus } from '../../../../common/enums/ProspectStatus';
import Icon, { IconType } from '../../../../components/Icon';
import { find, includes } from 'ramda';
import IconButton from '../../../../components/IconButton';
import TextOverflowTooltip from '../../../../components/TextOverflowTooltip';
import { IProspect } from '../../../../common/models/prospect/IProspect';
import { formatPhoneNumber } from '../../../../common/helpers/phoneHelper';
import styles from './styles.module.scss';
import UserLogo from '../../../../components/UserLogo';
import EmptyFieldPlaceholder from '../../../../components/EmptyFieldPlaceholder';

const getProfileLinkIcon = (profileLink: string) => {
  const links = ['facebook.com', 'linkedin.com', 'twitter.com'];
  const foundLink = find(link => includes(link, profileLink), links);
  if (foundLink) {
    return <Icon icon={foundLink.slice(0, -4) as IconType} className="hover-icon-fill" />;
  }
  return profileLink ? (<Icon icon="link" className="hover-icon-fill" />) : undefined;
};

export const getColumns = (search: string, changeStatus: IBindingCallback1<IProspectStatus>): Column[] => [
  {
    Header: () => <FormattedMessage id="dateAdded" defaultMessage="Date added" />,
    accessor: 'optInDate',
    width: 130,
    Cell: ({ cell: { value } }) => formatDate(value),
    sortType: SortType.Date
  },
  {
    Header: () => <FormattedMessage id="prospectTable.prospectName" defaultMessage="Prospect Name" />,
    id: 'prospectName',
    width: 180,
    Cell: ({ row }) => {
      const { firstName, lastName, candidateFirstName, candidateLastName } = row.original as IProspect;
      return (
        <div>
          <TextOverflowTooltip content={`${firstName} ${lastName}`} width={225}>
            <Highlighter
              highlightClassName="highlighted-text"
              searchWords={[search]}
              textToHighlight={`${firstName} ${lastName}`}
              className="cell-active"
              autoEscape
            />
          </TextOverflowTooltip>
          <TextOverflowTooltip content={`${candidateFirstName}, ${candidateLastName}`} width={225}>
            <span className="description mt-1">
              <FormattedMessage id="source" defaultMessage="Source" />
              :&nbsp;
              {`${candidateFirstName} ${candidateLastName}`}
            </span>
          </TextOverflowTooltip>
        </div>
      );
    }
  },
  {
    Header: () => <FormattedMessage id="contactInfo" defaultMessage="Contact info" />,
    id: 'contactInfo',
    width: 255,
    Cell: ({ row }) => {
      const { email, phoneNumber } = row.original as IProspect;
      return (
        <div>
          {email && (
            <span className="d-flex align-items-center">
              <Icon className={styles.contactInfoIcon} icon="email" />
              <TextOverflowTooltip content={email} width={225}>
                <Highlighter
                  highlightClassName="highlighted-text"
                  searchWords={[search]}
                  textToHighlight={email}
                  autoEscape
                />
              </TextOverflowTooltip>
            </span>
          )}
          {phoneNumber && (
            <span className="mt-1 d-flex align-items-center">
              <Icon className={styles.contactInfoIcon} icon="phone" />
              <TextOverflowTooltip content={formatPhoneNumber(phoneNumber)} width={225}>
                <Highlighter
                  highlightClassName="highlighted-text"
                  searchWords={[search]}
                  textToHighlight={formatPhoneNumber(phoneNumber)}
                  autoEscape
                />
              </TextOverflowTooltip>
            </span>
          )}
        </div>
      );
    }
  },
  {
    Header: () => (
      <FormattedMessage
        id="currentEmployer"
        defaultMessage="Current or recent employer"
      />
    ),
    accessor: (item: any) => item.currentCompany,
    id: 'currentCompany',
    Cell: ({ row }) => {
      const { currentCompany, workEndDate, workStartDate } = row.original as IProspect;
      return (
        <div>
          <TextOverflowTooltip content={currentCompany} width={265}>
            {currentCompany ? (
              <Highlighter
                highlightClassName="highlighted-text"
                searchWords={[search]}
                textToHighlight={currentCompany}
                autoEscape
              />
            ) : <EmptyFieldPlaceholder />}
          </TextOverflowTooltip>
          {workStartDate && (
            <span className="description mt-1">
              {getFullYear(workStartDate)}
              &nbsp;-&nbsp;
              {workEndDate
                ? getFullYear(workEndDate)
                : <FormattedMessage id="present" defaultMessage="Present" />}
            </span>
          )}
        </div>
      );
    },
    width: 210
  },
  {
    Header: () => <FormattedMessage id="currentRole" defaultMessage="Current or recent role" />,
    accessor: (item: any) => item.currentPosition,
    id: 'currentPosition',
    width: 180,
    Cell: ({ cell: { value } }) => (
      <TextOverflowTooltip content={value} width={180}>
        {value ? (
          <Highlighter
            highlightClassName="highlighted-text"
            searchWords={[search]}
            textToHighlight={value}
            autoEscape
          />
        ) : <EmptyFieldPlaceholder />}
      </TextOverflowTooltip>
    )
  },
  {
    Header: () => <FormattedMessage id="prospectTable.profile" defaultMessage="Profile" />,
    id: 'profileLink',
    width: 80,
    Cell: ({ row: { original: { profileLink } } }: any) => (
      <div style={{ maxWidth: '40px' }} className="table-center">
        {profileLink
          // eslint-disable-next-line react/jsx-no-target-blank
          ? <a href={profileLink} target="_blank">{getProfileLinkIcon(profileLink)}</a>
          : <EmptyFieldPlaceholder />}
      </div>
    )
  },
  {
    Header: () => <FormattedMessage id="candidateTable.assignedUser" defaultMessage="Assigned user" />,
    id: 'owner',
    width: 125,
    disableFilters: true,
    Cell: ({ row }) => {
      const { ownerFirstName, ownerId, ownerInitials, ownerLastName } = row.original as IProspect;
      return (
        <div style={{ maxWidth: '90px' }} className="table-center cursor-pointer">
          {ownerId
            ? (
              <UserLogo
                user={{
                  id: ownerId,
                  firstName: ownerFirstName as string,
                  lastName: ownerLastName as string,
                  initials: ownerInitials as string,
                  isSupport: false
                }}
              />
            )
            : <EmptyFieldPlaceholder />}
        </div>
      );
    }
  },
  {
    Header: () => <FormattedMessage id="prospectTable.archive" defaultMessage="Archive" />,
    id: 'archive',
    width: 80,
    disableSortBy: true,
    disableFilters: true,
    accessor: '-',
    Cell: ({ row }) => {
      const original = row.original as IProspect;
      const isArchive = original.status === ProspectStatus.Active;
      return (
        <div className="table-actions table-center" style={{ maxWidth: '50px' }}>
          <IconButton
            variant="blue"
            icon={<Icon icon={isArchive ? 'archive' : 'unarchive'} />}
            onClick={() => changeStatus({
              ids: [original.contactId],
              status: isArchive ? ProspectStatus.Archived : ProspectStatus.Active
            })}
            tooltip={isArchive
              ? { id: 'iconButtonLable.archive', defaultMessage: 'Archive / Inactive' }
              : { id: 'iconButtonLable.unarchive', defaultMessage: 'Unarchive / Active' }}
          />
        </div>
      );
    }
  }
];
